import { useState, useEffect } from "react";
import { Autocomplete } from "./";
import { useUser } from "../hooks/query";

interface User {
    Id: string;
    FirstName: string;
    LastName: string;
    Email: string;
    IsActive?: boolean;
}

interface UserDropdownProps {
    name: string;
    label: string;
    placeholder?: string;
    rules?: object;
    helperText?: string;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    filterActive?: boolean; // Only show active users
}

function UserDropdown({
    name,
    label,
    placeholder = "Select user",
    rules = {},
    helperText,
    size = "md",
    disabled = false,
    filterActive = true, // Default to showing only active users
    ...props
}: UserDropdownProps) {
    const { useList } = useUser();
    const listUsers = useList();

    const [users, setUsers] = useState<User[]>([]);

    // Fetch users on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await listUsers.mutateAsync({});
                if (response && Array.isArray(response)) {
                    // Filter active users if needed
                    const filteredUsers = filterActive
                        ? response.filter((user: User) => user.IsActive !== false)
                        : response;
                    setUsers(filteredUsers);
                }
            } catch (error) {
                console.error("Error fetching users:", error);
                setUsers([]);
            }
        };

        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterActive]);

    // Transform users for autocomplete
    const userOptions = users.map((user) => ({
        value: user.Email,
        label: user.Email,
        ...user,
    }));

    return (
        <Autocomplete
            name={name}
            label={label}
            placeholder={placeholder}
            rules={rules}
            helperText={helperText}
            size={size}
            options={userOptions}
            disabled={disabled || listUsers.isPending}
            loading={listUsers.isPending}
            // Ensure Autocomplete can match the current value to an option
            isOptionEqualToValue={(option: any, value: any) => {
                if (!option || !value) return false;
                return option.value === value.value;
            }}
            // Ensure label renders correctly from option objects
            getOptionLabel={(option: any) => {
                if (typeof option === "string") return option;
                return option.label ?? "";
            }}
            {...props}
        />
    );
}

export { UserDropdown };
